import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import { ExpiredTokenError, InvalidTokenError, UserNotFound } from "@/errors/auth.errors";
import { checkIfTokenIsExpired, decodeToken, generateDecodedToken } from "@/helpers/auth-helpers";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useIdleTimeout from "@/hooks/use-idle-timeout";
import useLoading from "@/hooks/use-loading";
import IUserBackoffice from "@/interfaces/user-backoffice.interfaces";
import authService from "@/services/auth.service";
import userBackofficeService from "@/services/user-backoffice.service";
import { AuthContextProps, AuthProviderProps, LoginProps } from "@/types/auth.types";
import { UserProfile } from "@/types/user.types";

import useLanguage from "./language-context";
import useToast from "./toast-context";

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const getAccessToken = (): string | null => {
  return localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { lang } = useLanguage();
  const { showToast } = useToast();

  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");
  const urlToRedirect = redirectUrl || ROUTE_URLS.serviceDashboard(lang);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<IUserBackoffice | null>(null);
  const [redirectUrlLogout, setRedirectUrlLogout] = useState("");

  const {
    isLoading: isLoggingOut,
    startLoading: startLoggingOut,
    endLoading: endLoggingOut,
  } = useLoading();

  const { isLoading, startLoading, endLoading, loadingIcon } = useLoading({
    initialState: true,
    loadingIconColor: "primary",
    loadingIconSize: 40,
  });

  const {
    isLoading: isLoggingIn,
    startLoading: startLoggingIn,
    endLoading: endLoggingIn,
    loadingIcon: loadingIconLoggingIn,
  } = useLoading();

  const isLoginPage = pathname.includes(ROUTE_URLS.loginBase);

  const redirectUser = (path: string) => {
    setRedirectUrlLogout(path);
    navigate(path, { replace: true });
  };

  const logout = (redirect?: string) => {
    startLoggingOut();

    localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
    localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
    localStorage.removeItem(LOCAL_STORAGE.expiredPassword);
    setIsAuthenticated(false);

    const redirectPath = redirect || (!isLoginPage ? ROUTE_URLS.login(lang) : "");

    if (redirectPath) {
      redirectUser(redirectPath);
    }

    endLoggingOut();
  };

  const getUser = useCallback(async (userId: number) => {
    try {
      const user = await userBackofficeService.getById(userId);
      setUser(user);

      if (!user) {
        throw new UserNotFound();
      }
    } catch (err) {
      const message = buildErrorMessage(err);
      // eslint-disable-next-line no-console
      console.error(message);
    }
  }, []);

  const login = async ({ formData, onError, onSuccess }: LoginProps): Promise<void> => {
    startLoggingIn();

    try {
      const { accessToken, refreshToken, expiredPassword } = await authService.login(formData);

      if (!accessToken) throw new InvalidTokenError();

      localStorage.setItem(LOCAL_STORAGE.accessTokenKey, accessToken);
      localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, refreshToken);
      if (typeof expiredPassword === "boolean") {
        localStorage.setItem(LOCAL_STORAGE.expiredPassword, String(expiredPassword));
      }

      const decodedToken = decodeToken(accessToken);
      if (checkIfTokenIsExpired(decodedToken)) throw new ExpiredTokenError();

      const user = await userBackofficeService.getById(Number(decodedToken.UserID));

      if (!user) {
        throw new UserNotFound();
      }

      setUser(user);
      setIsAuthenticated(true);

      const shouldForcePasswordChange =
        localStorage.getItem(LOCAL_STORAGE.expiredPassword) === "true";
      if (shouldForcePasswordChange) {
        navigate(ROUTE_URLS.changePassword(lang), { replace: true });
      } else {
        navigate(urlToRedirect, { replace: true });
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      logout();
      const message = buildErrorMessage(err);
      showToast({ text: message, type: "error" });
      if (onError) onError();
    } finally {
      endLoggingIn();
    }
  };

  const checkPermissions = (requiredProfiles: UserProfile[]) => {
    const hasNoPermission = !requiredProfiles.some((profile) =>
      user?.userProfile.some((userProfile) => userProfile.profileId === profile)
    );

    return hasNoPermission;
  };

  const handleIdleTimeout = useCallback(() => {
    logout();
  }, [logout]);

  useIdleTimeout(handleIdleTimeout, isAuthenticated);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    const accessToken = getAccessToken();
    startLoading();

    const checkIfUserIsLoggedIn = (): boolean => {
      if (!accessToken) {
        return false;
      }
      const decodedToken = generateDecodedToken(accessToken);
      if (!decodedToken) {
        return false;
      }

      if (checkIfTokenIsExpired(decodedToken)) {
        return false;
      }

      return true;
    };

    const isCurrentlyAuthenticated = checkIfUserIsLoggedIn();
    if (isCurrentlyAuthenticated === isAuthenticated) {
      endLoading();
      return;
    }
    setIsAuthenticated(isCurrentlyAuthenticated);
    endLoading();
  }, [endLoading, startLoading, isAuthenticated, lang, isLoggingOut]);

  useEffect(() => {
    // This useEffect checks authentication and redirects, including expired password flow
    const isExpiredPassword = localStorage.getItem(LOCAL_STORAGE.expiredPassword) === "true";
    const isChangePasswordPage = pathname === ROUTE_URLS.changePassword(lang);

    if (!isLoading) {
      if (isAuthenticated) {
        if (isExpiredPassword && !isChangePasswordPage) {
          navigate(ROUTE_URLS.changePassword(lang), { replace: true });
          return;
        }
        if (isLoginPage) {
          navigate(isExpiredPassword ? ROUTE_URLS.changePassword(lang) : urlToRedirect, { replace: true });
        }
      } else if (!isLoginPage) {
        if (redirectUrlLogout !== "") {
          navigate(redirectUrlLogout, { replace: true });
        } else {
          navigate(ROUTE_URLS.loginRedirect(pathname, lang), { replace: true });
        }
      }
    }
  }, [
    isAuthenticated,
    isLoginPage,
    lang,
    pathname,
    navigate,
    urlToRedirect,
    redirectUrlLogout,
    isLoading,
  ]);

  useEffect(() => {
    // This useEffect gets user and saves in the user state
    if (typeof localStorage === "undefined") return;
    const accessToken = getAccessToken();
    if (!accessToken) return;
    const decodedToken = generateDecodedToken(accessToken);
    if (!decodedToken) return;
    getUser(Number(decodedToken.UserID));
  }, [getUser]);

  const isLoadingPathProtected = (isLoading || !isAuthenticated) && !isLoginPage;

  const isLoadingLoginPage = (isLoading || isAuthenticated) && isLoginPage;

  if (isLoadingLoginPage || isLoggingOut || isLoadingPathProtected)
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loadingIcon}
      </div>
    );
  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated,
        isLoggingOut,
        logout,
        loadingIcon,
        user,
        login,
        loadingIconLoggingIn,
        isLoggingIn,
        checkPermissions,
      }}
    >
      {isLoadingLoginPage || isLoggingOut || isLoadingPathProtected ? (
        <div
          style={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loadingIcon}
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);
export default useAuth;
